import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { MAX_PREVIEW_IMAGES } from '../constants/form';
import {
  getOrientation,
  getRemoteMediaDimensions,
  type ImageDimensions,
} from '../utils/imageDimensions';
import {
  hasAnyPreview,
  type PreviewOrientation,
  validateBannerDimensions,
  validateEditorsChoiceGraphic,
  validatePreviewDimensions,
} from '../utils/imageValidation';

interface UsePreviewFileHandlerProps {
  formPreviews: (File | null)[];
  existingUrls?: (string | null)[];
  onFileChange: (index: number, file: File | null) => void;
  onDimensionsChange?: (dimensions: ImageDimensions | null) => void;
  onFormTouched?: () => void;
}

interface UseSingleFileHandlerProps {
  onFileChange: (
    fieldName: 'dappIcon' | 'banner' | 'editorsChoiceGraphic',
    file: File | null,
  ) => void;
  onFormTouched?: () => void;
}

/** Everything the dApp Preview field needs from the page: orientation + slot handlers. */
export type PreviewUploadsController = ReturnType<typeof usePreviewFileHandler>;

export const usePreviewFileHandler = ({
  formPreviews,
  existingUrls,
  onFileChange,
  onDimensionsChange,
  onFormTouched,
}: UsePreviewFileHandlerProps) => {
  const [previewDimensions, setPreviewDimensions] =
    useState<ImageDimensions | null>(null);
  const [selectedOrientation, setOrientation] =
    useState<PreviewOrientation>('portrait');
  const [probeFailed, setProbeFailed] = useState(false);
  const firstExistingUrl = existingUrls?.find((url) => url !== null) ?? null;
  const hasFilePreview = formPreviews.some((preview) => preview !== null);
  const isOrientationLocked = Boolean(firstExistingUrl) || hasFilePreview;
  /** Saved previews exist but their dimensions are still being fetched. */
  const isResolvingOrientation =
    Boolean(firstExistingUrl) && previewDimensions === null && !probeFailed;
  const orientation: PreviewOrientation = previewDimensions
    ? getOrientation(previewDimensions) === 'landscape'
      ? 'landscape'
      : 'portrait'
    : selectedOrientation;

  useEffect(() => {
    if (!firstExistingUrl) {
      return;
    }

    let cancelled = false;
    setPreviewDimensions(null);
    setProbeFailed(false);

    void getRemoteMediaDimensions(firstExistingUrl)
      .then((dimensions) => {
        if (!cancelled) {
          setPreviewDimensions(dimensions);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setProbeFailed(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [firstExistingUrl]);

  useEffect(() => {
    if (!firstExistingUrl && !hasFilePreview) {
      setPreviewDimensions(null);
    }
  }, [firstExistingUrl, hasFilePreview]);

  const resolveBaseline = async (): Promise<
    { ok: true; baseline: ImageDimensions | null } | { ok: false }
  > => {
    const isFirstPreview = !hasAnyPreview(formPreviews, existingUrls);
    if (isFirstPreview) {
      return { ok: true, baseline: null };
    }

    let baseline = previewDimensions;
    if (!baseline && firstExistingUrl) {
      try {
        baseline = await getRemoteMediaDimensions(firstExistingUrl);
        setPreviewDimensions(baseline);
      } catch {
        toast.error('Failed to validate existing preview dimensions');
        return { ok: false };
      }
    }

    if (!baseline) {
      toast.error('Wait for existing previews to finish loading');
      return { ok: false };
    }

    return { ok: true, baseline };
  };

  const handlePreviewFileChange = async (index: number, file: File | null) => {
    onFormTouched?.();

    if (!file) {
      onFileChange(index, null);

      if (!hasAnyPreview(formPreviews, existingUrls, index)) {
        setPreviewDimensions(null);
        onDimensionsChange?.(null);
      }
      return;
    }

    const resolved = await resolveBaseline();
    if (!resolved.ok) {
      return;
    }

    const validation = await validatePreviewDimensions(
      file,
      resolved.baseline,
      resolved.baseline ? undefined : orientation,
    );

    if (!validation.isValid) {
      return;
    }

    if (!resolved.baseline && validation.dimensions) {
      setPreviewDimensions(validation.dimensions);
      onDimensionsChange?.(validation.dimensions);
    }

    onFileChange(index, file);
  };

  /**
   * Drop-zone path: fills empty slots in order. The first accepted file sets
   * the baseline the rest must match.
   */
  const handlePreviewFilesChange = async (files: File[]) => {
    onFormTouched?.();

    const resolved = await resolveBaseline();
    if (!resolved.ok) {
      return;
    }

    const isFilled = (index: number) =>
      Boolean(formPreviews[index]) || Boolean(existingUrls?.[index]);
    let baseline = resolved.baseline;
    let nextIndex = 0;

    for (const file of files) {
      while (nextIndex < MAX_PREVIEW_IMAGES && isFilled(nextIndex)) {
        nextIndex += 1;
      }
      if (nextIndex >= MAX_PREVIEW_IMAGES) {
        toast.error(`You can upload up to ${MAX_PREVIEW_IMAGES} previews`);
        return;
      }

      const validation = await validatePreviewDimensions(
        file,
        baseline,
        baseline ? undefined : orientation,
      );
      if (!validation.isValid) {
        continue;
      }

      if (!baseline && validation.dimensions) {
        baseline = validation.dimensions;
        setPreviewDimensions(baseline);
        onDimensionsChange?.(baseline);
      }

      onFileChange(nextIndex, file);
      nextIndex += 1;
    }
  };

  return {
    handlePreviewFileChange,
    handlePreviewFilesChange,
    previewDimensions,
    orientation,
    isOrientationLocked,
    isResolvingOrientation,
    setOrientation,
  };
};

export const useSingleFileHandler = ({
  onFileChange,
  onFormTouched,
}: UseSingleFileHandlerProps) => {
  const handleSingleFileChange = async (
    fieldName: 'dappIcon' | 'banner' | 'editorsChoiceGraphic',
    file: File | null,
  ) => {
    onFormTouched?.();

    if (fieldName === 'editorsChoiceGraphic' && file) {
      const validation = await validateEditorsChoiceGraphic(file);
      if (!validation.isValid) {
        return;
      }
    }

    if (fieldName === 'banner' && file) {
      const validation = await validateBannerDimensions(file);
      if (!validation.isValid) {
        return;
      }
    }

    onFileChange(fieldName, file);
  };

  return handleSingleFileChange;
};
